import { useRef, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import { useToast } from "../context/toast-context";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { formatFileName } from "../utils/format";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

interface SelfieUploaderProps {
  onFileChange: (file: File) => void;
  fileName?: string;
}

export function SelfieUploader({
  onFileChange,
  fileName,
}: SelfieUploaderProps) {
  const [selfieImage, setSelfieImage] = useState<{
    file: File | null;
    error: string;
  }>({
    file: null,
    error: "",
  });
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const validateImage = (file: File): { isValid: boolean; error: string } => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        isValid: false,
        error: t("images.documents.type"),
      };
    }

    return { isValid: true, error: "" };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { error, isValid } = validateImage(file);
    if (!isValid) {
      setSelfieImage((prev) => ({ ...prev, error }));
      return;
    }

    setSelfieImage({ file, error: "" });
    onFileChange(file);
  };

  const startCamera = async () => {
    try {
      setIsCameraOpen(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      // Esperamos un poco para que se cargue la cámara
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
          };
        }
      }, 100);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        showToast(
          "Si estamos del browser no se puede acceder a la cámara",
          "error"
        );
      }
      setSelfieImage((prev) => ({
        ...prev,
        error: t("employee.verify.document.errors.camera"),
      }));
      setIsCameraOpen(false);
    }
  };

  const takePicture = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], formatFileName(fileName, "selfie.jpg"), {
          type: "image/jpeg",
        });
        setSelfieImage({ file, error: "" });
        onFileChange(file);
        stopCamera();
      }
    }, "image/jpeg");
  };

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject as MediaStream;
    stream?.getTracks().forEach((track) => track.stop());
    setIsCameraOpen(false);
  };

  return (
    <div className="">
      <div className="flex flex-col gap-2">
        {isMobile && !isCameraOpen && (
          <button
            type="button"
            onClick={startCamera}
            className="w-full text-sm border bg-primary text-white rounded-full"
          >
            {t("employee.verify.document.selfie-camera")}
          </button>
        )}

        <label
          htmlFor="selfie"
          className="block text-sm border-2 border-primary bg-white text-primary rounded-full text-center cursor-pointer"
        >
          {t("employee.verify.document.selfie-upload")}
        </label>
      </div>

      <Dialog
        open={isCameraOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          stopCamera();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            padding: "20px 24px",
            fontWeight: 400,
            fontSize: "16px",
            borderRadius: "30px",
            "&.MuiDialogTitle-root": {
              fontFamily: "Montserrat",
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "20px",
              fontFamily: "Montserrat",
            }}
          >
            {t("employee.verify.document.title")}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            fontFamily: "Montserrat",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full rounded-lg"
            style={{ maxHeight: "70vh" }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", gap: 1 }}>
          <Button
            sx={{
              fontFamily: "Montserrat",
              backgroundColor: "#FF0000",
              color: "#fff",
              fontSize: "0.875rem",
              textTransform: "capitalize",
              fontWeight: 300,
              textAlign: "center",
              width: "120px",
            }}
            onClick={stopCamera}
            color="error"
          >
            {t("employee.verify.document.selfie-stop")}
          </Button>
          <Button
            sx={{
              fontFamily: "Montserrat",
              backgroundColor: "#EF0BB8",
              color: "#fff",
              fontSize: "0.875rem",
              textTransform: "capitalize",
              fontWeight: 300,
              textAlign: "center",
              width: "120px",
            }}
            onClick={takePicture}
            color="primary"
          >
            {t("employee.verify.document.selfie-take")}
          </Button>
        </DialogActions>
      </Dialog>

      <input
        type="file"
        id="selfie"
        name="selfie"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="text-xs break-words mt-1 w-full">
        {selfieImage.file
          ? selfieImage.file.name
          : t("images.documents.no-file")}
      </div>

      {selfieImage.error && (
        <p className="text-red text-xs mt-1">
          {t("employee.verify.document.errors.selfie")}
        </p>
      )}
    </div>
  );
}
